import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  ArrowRight, TrendingUp, Receipt, FileSpreadsheet, Sparkles,
  AlertCircle, ShieldCheck,
} from "lucide-react";
import { InstallPWA } from "@/components/InstallPWA";
import { AccountrixLogo } from "@/components/AccountrixLogo";

// iter 72m — "Botanical Ledger" login page. Deep-forest hero on the right,
// alabaster form column on the left, leaf-green accents throughout.

const HIGHLIGHTS = [
  { icon: TrendingUp,      title: "Track income, expenses & bank feeds",
    desc: "Real-time double-entry sync across bank connections and personal ledgers." },
  { icon: Receipt,         title: "Invoices, bills, payroll & inventory",
    desc: "End-to-end commercial operations without bloated software overhead." },
  { icon: FileSpreadsheet, title: "P&L, Balance Sheet & Cash Flow reports",
    desc: "Instant GAAP-compliant statutory statements with drill-down." },
  { icon: Sparkles,        title: "AI categorisation & anomaly alerts",
    desc: "Proactive audit intelligence catches discrepancies before month-end close." },
];

export default function Login() {
  const { login, startDemo, error, setError } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [demoLoading, setDemoLoading] = useState(false);
  const navigate = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    const ok = await login(email, password);
    setLoading(false);
    if (ok) navigate("/");
  };
  const demo = async () => {
    setDemoLoading(true);
    const ok = await startDemo();
    setDemoLoading(false);
    if (ok) navigate("/");
  };

  return (
    <div
      className="min-h-screen bg-[#FAFCFA] grid grid-cols-1 lg:grid-cols-12"
      data-testid="login-page"
      style={{ fontFamily: "'IBM Plex Sans', system-ui, sans-serif" }}
    >
      {/* ─────────── LEFT · Sign-in form ─────────── */}
      <section className="col-span-1 lg:col-span-5 xl:col-span-5 flex flex-col justify-between px-6 sm:px-12 xl:px-16 py-8 sm:py-10 relative z-10 bg-[#FAFCFA] border-r border-[#E5EDE7]">
        {/* Brand */}
        <AccountrixLogo variant="full" size="md" testId="brand-mark" />

        {/* Form column */}
        <div className="max-w-md w-full mx-auto lg:mx-0 flex-1 flex flex-col justify-center py-10">
          <div className="inline-flex items-center gap-2 self-start px-3 py-1 rounded-full bg-[#E8F5EE] border border-[#C2DECF] mb-6">
            <ShieldCheck className="w-3.5 h-3.5 text-[#2F6B4A]" />
            <span className="text-[10px] tracking-[0.2em] uppercase font-semibold text-[#2F6B4A]">
              Trusted personal + business ledger
            </span>
          </div>
          <h1
            className="font-display text-3xl sm:text-4xl xl:text-5xl font-semibold tracking-tight text-[#0F2018] leading-[1.1]"
            style={{ fontFamily: "'Outfit', system-ui, sans-serif" }}
          >
            Welcome back.
          </h1>
          <p className="mt-3 text-sm text-[#3D5247] leading-relaxed">
            Sign in to your Accountrix workspace — the accounting suite for people who take their books seriously.
          </p>

          <form onSubmit={submit} className="mt-8 space-y-5" data-testid="login-form">
            <div>
              <Label
                htmlFor="email"
                className="text-xs font-semibold tracking-wider uppercase text-[#3D5247]"
              >
                Email
              </Label>
              <Input
                id="email"
                type="email"
                required
                autoComplete="email"
                data-testid="login-email"
                value={email}
                onChange={(e) => { setEmail(e.target.value); if (error) setError(null); }}
                placeholder="you@company.com"
                className="mt-1.5 h-11 rounded-lg border-[#D3E0D8] bg-white text-[#0F2018] placeholder:text-[#889B90] focus-visible:ring-2 focus-visible:ring-[#3A8760] focus-visible:border-[#3A8760] shadow-sm transition-all"
              />
            </div>

            <div>
              <div className="flex items-center justify-between">
                <Label
                  htmlFor="password"
                  className="text-xs font-semibold tracking-wider uppercase text-[#3D5247]"
                >
                  Password
                </Label>
                <Link
                  to="/forgot-password"
                  data-testid="forgot-password-link"
                  className="text-xs text-[#2F6B4A] hover:text-[#132A1F] font-semibold underline-offset-4 hover:underline transition-colors"
                >
                  Forgot?
                </Link>
              </div>
              <Input
                id="password"
                type="password"
                required
                autoComplete="current-password"
                data-testid="login-password"
                value={password}
                onChange={(e) => { setPassword(e.target.value); if (error) setError(null); }}
                placeholder="••••••••"
                className="mt-1.5 h-11 rounded-lg border-[#D3E0D8] bg-white text-[#0F2018] placeholder:text-[#889B90] focus-visible:ring-2 focus-visible:ring-[#3A8760] focus-visible:border-[#3A8760] shadow-sm transition-all"
              />
            </div>

            {error && (
              <div
                data-testid="login-error"
                role="alert"
                className="text-sm text-[#991B1B] bg-[#FEF2F2] border border-[#FCA5A5] rounded-lg px-3.5 py-2.5 flex items-start gap-2 shadow-xs"
              >
                <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
                <span>{error}</span>
              </div>
            )}

            <Button
              type="submit"
              disabled={loading}
              data-testid="sign-in-btn"
              className="w-full h-11 rounded-lg bg-[#3A8760] hover:bg-[#2F6B4A] active:bg-[#245339] text-white font-medium shadow-md shadow-[#3A8760]/25 hover:shadow-lg hover:shadow-[#3A8760]/35 transition-all cursor-pointer disabled:opacity-60"
            >
              {loading ? "Signing in…" : (<>Sign in <ArrowRight className="ml-2 w-4 h-4" /></>)}
            </Button>
          </form>

          <div className="flex items-center gap-3 my-6">
            <div className="flex-1 h-px bg-[#E0E8E3]" />
            <span className="text-[10px] uppercase tracking-[0.25em] text-[#889B90] font-medium">or</span>
            <div className="flex-1 h-px bg-[#E0E8E3]" />
          </div>

          <Button
            type="button"
            variant="outline"
            onClick={demo}
            disabled={demoLoading}
            data-testid="try-demo-btn"
            className="w-full h-11 rounded-lg border border-[#C2DECF] bg-white hover:bg-[#F0F7F3] text-[#275E43] font-medium hover:border-[#3A8760] transition-all cursor-pointer disabled:opacity-60"
          >
            {demoLoading ? "Loading sandbox…" : "Try the demo — no signup"}
          </Button>

          <p className="mt-8 text-sm text-[#3D5247] text-center lg:text-left">
            New here?{" "}
            <Link
              to="/register"
              data-testid="register-link"
              className="text-[#2F6B4A] hover:text-[#132A1F] font-semibold underline-offset-4 hover:underline transition-colors"
            >
              Create an account
            </Link>
          </p>

          <div className="mt-6" data-testid="login-install-slot">
            <InstallPWA variant="prominent" testId="login-install-pwa" />
          </div>
        </div>

        {/* Legal footer */}
        <div className="text-xs text-[#62776D] pt-6 border-t border-[#E5EDE7]">
          By continuing you agree to our{" "}
          <Link to="/terms" className="hover:text-[#2F6B4A] transition-colors underline-offset-2 hover:underline">Terms</Link>{" "}·{" "}
          <Link to="/privacy" className="hover:text-[#2F6B4A] transition-colors underline-offset-2 hover:underline">Privacy</Link>
        </div>
      </section>

      {/* ─────────── RIGHT · Botanical hero ─────────── */}
      <section
        data-testid="login-hero"
        className="hidden lg:flex col-span-1 lg:col-span-7 xl:col-span-7 relative flex-col justify-between overflow-hidden p-12 xl:p-16 text-white"
        style={{ background: "linear-gradient(135deg, #0C1E16 0%, #143224 45%, #1A402E 100%)" }}
      >
        {/* Subtle grid overlay */}
        <div
          aria-hidden
          className="absolute inset-0 opacity-[0.05] pointer-events-none"
          style={{
            backgroundImage: "linear-gradient(#ffffff 1px, transparent 1px), linear-gradient(90deg, #ffffff 1px, transparent 1px)",
            backgroundSize: "44px 44px",
          }}
        />
        {/* Ambient glow */}
        <div aria-hidden className="absolute -right-40 -top-40 w-[520px] h-[520px] rounded-full bg-[#3A8760]/20 blur-[120px] pointer-events-none" />
        <div aria-hidden className="absolute -left-32 -bottom-32 w-[380px] h-[380px] rounded-full bg-[#4EAC7B]/15 blur-[100px] pointer-events-none" />

        {/* Organic leaf-vein watermark SVG */}
        <svg
          aria-hidden
          className="absolute -right-24 -bottom-24 w-[620px] h-[620px] opacity-[0.09] pointer-events-none"
          viewBox="0 0 620 620"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M310 40 C 480 100, 570 260, 520 430 C 450 560, 240 590, 130 480 C 50 380, 90 200, 220 100 C 260 70, 285 55, 310 40 Z"
            stroke="#7ED4A6"
            strokeWidth="1.4"
          />
          <path d="M310 40 L 310 580" stroke="#7ED4A6" strokeWidth="1.2" opacity="0.9" />
          <path d="M310 130 L 200 200" stroke="#7ED4A6" strokeWidth="1" opacity="0.6" />
          <path d="M310 130 L 420 200" stroke="#7ED4A6" strokeWidth="1" opacity="0.6" />
          <path d="M310 240 L 170 320" stroke="#7ED4A6" strokeWidth="1" opacity="0.5" />
          <path d="M310 240 L 450 320" stroke="#7ED4A6" strokeWidth="1" opacity="0.5" />
          <path d="M310 360 L 200 440" stroke="#7ED4A6" strokeWidth="1" opacity="0.4" />
          <path d="M310 360 L 420 440" stroke="#7ED4A6" strokeWidth="1" opacity="0.4" />
        </svg>

        <div className="relative z-10 flex flex-col justify-between h-full w-full max-w-2xl">
          {/* Top row · trust pill */}
          <div className="flex items-center gap-3">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#1A3D2D]/80 border border-[#3A8760]/40 text-[#A2E2BF] text-xs font-medium backdrop-blur-md">
              <span className="w-1.5 h-1.5 rounded-full bg-[#4EAC7B] animate-pulse" />
              Live · Preview + Production
            </div>
          </div>

          {/* Hero copy */}
          <div className="my-auto">
            <div
              className="text-xs font-medium tracking-[0.22em] uppercase text-[#7ED4A6]"
            >
              The accounting suite for personal + business books
            </div>
            <h2
              className="font-display text-4xl xl:text-5xl 2xl:text-6xl font-semibold mt-4 leading-[1.1] tracking-tight"
              style={{ fontFamily: "'Outfit', system-ui, sans-serif" }}
            >
              The books,<br />done right.
            </h2>
            <p className="mt-5 text-base text-[#D4E8DC] leading-relaxed max-w-lg">
              One workspace for your personal finances and your business books —
              journals, invoices, aged AR, payroll, and reports you can trust.
            </p>

            {/* Bento feature grid */}
            <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 gap-3 max-w-xl">
              {HIGHLIGHTS.map(({ icon: Icon, title, desc }) => (
                <div
                  key={title}
                  className="bg-white/[0.06] border border-white/10 backdrop-blur-md rounded-xl p-4 shadow-[0_8px_30px_rgba(0,0,0,0.18)] transition-all hover:bg-white/[0.09] hover:border-[#4EAC7B]/30"
                >
                  <div className="flex items-start gap-3">
                    <div className="w-8 h-8 rounded-lg bg-[#3A8760]/25 border border-[#3A8760]/40 text-[#A2E2BF] flex items-center justify-center shrink-0">
                      <Icon className="w-4 h-4" />
                    </div>
                    <div className="min-w-0">
                      <div className="text-sm font-semibold text-white leading-snug">{title}</div>
                      <div className="text-xs text-[#B4D6C1] mt-1 leading-snug">{desc}</div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Bottom · trust caption */}
          <div className="pt-8 border-t border-white/10 flex items-center justify-between gap-4 flex-wrap">
            <div className="text-xs text-[#8AAF97]">
              Trusted by solo operators, side-hustlers and finance teams alike.
            </div>
            <div className="flex items-center gap-3 text-[10px] uppercase tracking-[0.22em] text-[#6E907C]">
              <span>Personal</span>
              <span className="w-1 h-1 rounded-full bg-[#3A8760]" />
              <span>Business</span>
              <span className="w-1 h-1 rounded-full bg-[#3A8760]" />
              <span>Multi-currency</span>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
